import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import type { SlaAction, SlaGrantDto } from '@/gen/types';
import { axiosSlaInstance } from '@/stores/middleware';

/**
 * The caller's SLA roles and grants, mirrored from GET sla/permissions — the frontend never
 * re-derives policy (ITDEVELOP-3230 phase 1, docs/sla-access.md). Fail closed: any error means
 * "no access", which hides the SLA/onboarding surface entirely.
 */
export const useSlaAccessStore = defineStore('slaAccess', () => {
    const roles = ref<string[]>([]);
    const grants = ref<SlaGrantDto[]>([]);
    const loaded = ref(false);
    const loading = ref(false);

    async function fetchOnce(): Promise<void> {
        if (loaded.value || loading.value) return;
        loading.value = true;
        try {
            const { data } = await axiosSlaInstance.get('/sla/permissions');
            roles.value = data?.data?.roles ?? [];
            grants.value = data?.data?.grants ?? [];
        } catch {
            // Fail closed — and stay loaded: a broken backend must not cause a fetch loop.
            // A re-login remounts the app and retries.
            roles.value = [];
            grants.value = [];
        } finally {
            loaded.value = true;
            loading.value = false;
        }
    }

    const hasAnyAccess = computed(() => grants.value.length > 0);

    /** Phase-1 mirror of the server rule: MyOrg never enables the internal surface. */
    function can(action: SlaAction): boolean {
        return grants.value.some((grant) => grant.action === action && grant.scope !== 'MyOrg');
    }

    return { roles, grants, loaded, loading, fetchOnce, hasAnyAccess, can };
});
